import type { GetCompanyTransactionsParams } from '@/services/companies.service'
import type { ScorecardPeriodType } from '@/services/scorecard.service'
import type { CompanyActualVsBudgetMetric } from '@/features/company-overview/actual-vs-budget'
import type { CompanySearch, DealLeadSearch } from '@/types/api'

// =============================================================================
// Companies Query Keys
// =============================================================================

export const companiesKeys = {
  all: ['companies'] as const,
  search: (params: Omit<CompanySearch, 'cursor'>) =>
    [...companiesKeys.all, 'search', params] as const,
  details: () => [...companiesKeys.all, 'detail'] as const,
  detail: (id: string) => [...companiesKeys.details(), id] as const,
  actualVsBudget: (
    companyId: string,
    metrics: readonly CompanyActualVsBudgetMetric[]
  ) =>
    [
      ...companiesKeys.detail(companyId),
      'actual-vs-budget',
      'FYTD',
      metrics.join(','),
    ] as const,
  transactions: (companyId: string, params: GetCompanyTransactionsParams) =>
    [...companiesKeys.detail(companyId), 'transactions', params] as const,
}

// =============================================================================
// Admin Query Keys
// =============================================================================

export const adminKeys = {
  all: ['admin'] as const,
  users: () => [...adminKeys.all, 'users'] as const,
  usersList: (params?: { limit?: number; cursor?: string }) => {
    const base = adminKeys.users()
    return params === undefined
      ? ([...base] as const)
      : ([...base, params] as const)
  },
  invitations: () => [...adminKeys.all, 'invitations'] as const,
  domains: () => [...adminKeys.all, 'domains'] as const,
  domainsByCompany: (companyId: string) =>
    [...adminKeys.domains(), companyId] as const,
}

// =============================================================================
// Deal Review Query Keys
// =============================================================================

export const dealLeadsKeys = {
  all: ['deal-leads'] as const,
  lists: () => [...dealLeadsKeys.all, 'list'] as const,
  /**
   * Inbox and History differ only by the filters they pass, so they share this
   * factory and cache under different filter keys rather than under different
   * resources.
   */
  list: (params: Omit<DealLeadSearch, 'cursor'>) =>
    [...dealLeadsKeys.lists(), params] as const,
  counts: () => [...dealLeadsKeys.all, 'counts'] as const,
  details: () => [...dealLeadsKeys.all, 'detail'] as const,
  detail: (id: string) => [...dealLeadsKeys.details(), id] as const,
  /** Server-owned form rules, keyed apart from any single lead. */
  decisionOptions: () => [...dealLeadsKeys.all, 'decision-options'] as const,
  fieldOptions: () => [...dealLeadsKeys.all, 'field-options'] as const,
  /** Intake health, which is about the mailbox rather than any lead. */
  pollStatus: () => [...dealLeadsKeys.all, 'poll-status'] as const,
  intakeActivity: () => [...dealLeadsKeys.all, 'intake-activity'] as const,
  intakeIssues: () => [...dealLeadsKeys.all, 'intake-issues'] as const,
  /**
   * Keyed so anything on the page can tell a re-screen is running, and keyed
   * by lead because the panel now hides its tabs while one runs: an unscoped
   * key made every other lead a reviewer opened claim to be screening too.
   */
  rescreen: (id: string) => [...dealLeadsKeys.all, 'rescreen', id] as const,
}

export const dealSendBatchesKeys = {
  all: ['deal-send-batches'] as const,
  active: () => [...dealSendBatchesKeys.all, 'active'] as const,
  detail: (id: string) => [...dealSendBatchesKeys.all, 'detail', id] as const,
  preview: (ids: string[]) =>
    [...dealSendBatchesKeys.all, 'preview', ids] as const,
}

export const dealSourceFilesKeys = {
  all: ['deal-source-files'] as const,
  emailBody: (sourceFileId: string) =>
    [...dealSourceFilesKeys.all, 'email-body', sourceFileId] as const,
  /** The raw bytes, cached so reopening a preview does not refetch them. */
  content: (sourceFileId: string) =>
    [...dealSourceFilesKeys.all, 'content', sourceFileId] as const,
}

// =============================================================================
// Scorecard Query Keys
// =============================================================================

export const scorecardKeys = {
  all: ['scorecard'] as const,
  company: (companyId: string) =>
    [...scorecardKeys.all, 'company', companyId] as const,
  portal: () => [...scorecardKeys.all, 'portal'] as const,
  metricOptions: (
    companyId: string,
    periodType: ScorecardPeriodType,
    includeMetricLabelId?: string
  ) =>
    [
      ...scorecardKeys.company(companyId),
      'metric-options',
      periodType,
      includeMetricLabelId ?? null,
    ] as const,
  metricOptionsForCompany: (companyId: string) =>
    [...scorecardKeys.company(companyId), 'metric-options'] as const,
}

// =============================================================================
// OGSM Query Keys
// =============================================================================

export const ogsmKeys = {
  all: ['ogsm'] as const,
  company: (companyId: string) =>
    [...ogsmKeys.all, 'company', companyId] as const,
  sessions: (companyId: string) =>
    [...ogsmKeys.company(companyId), 'sessions'] as const,
  board: (companyId: string, sessionId: string) =>
    [...ogsmKeys.company(companyId), 'board', sessionId] as const,
  assignees: (companyId: string) =>
    [...ogsmKeys.company(companyId), 'assignees'] as const,
  actuals: (companyId: string, nodeId: string) =>
    [...ogsmKeys.company(companyId), 'actuals', nodeId] as const,
  removalImpact: (companyId: string, nodeId: string) =>
    [...ogsmKeys.company(companyId), 'removal-impact', nodeId] as const,
}
